import React, { useEffect, useReducer } from 'react';
import { useMutation } from '@apollo/client';
import I18n from 'modules/i18n';
import Dropdown from '@/components/common/Dropdown';
import Icon from '@/components/ui/Icon';
import BackgroundPositionManager from '@/components/common/BackgroundPositionManager';
import { SlideObject } from '@/components/admin/SlideShow/types';
import useSlideShowContext from '../../stores/useSlideShowContext';
import { UPDATE_IMAGE_MODEL_OBJECT_POSITION } from '../../Utils/slideShowGraphQL';
import inertPositionAxes from '../../Utils/inertPositionAxes';

const DEFAULT_OBJECT_POSITION = 'center center';
// Custom positions are stored as an object-position `"X% Y%"` string (0-100 per axis).
const CUSTOM_RE = /^\d+(\.\d+)?% \d+(\.\d+)?%$/;
const IMAGE_SELECTOR = '.slideObjectImageContainer img';

type CustomValues = { backgroundPosition: string, customPosition: string, customPositionY: string };

type Props = {
  slideObject: SlideObject;
  styleDivRef: React.MutableRefObject<HTMLDivElement>;
};

const ChangeImagePosition = ({ slideObject, styleDivRef }: Props) => {
  const [updateImageModelMutation] = useMutation(UPDATE_IMAGE_MODEL_OBJECT_POSITION);
  const updateSlideObject = useSlideShowContext(state => state.updateSlideObject);

  const { imageModel } = slideObject;

  // inertAxes reads the img's dimensions during render, so recompute once a late-loading image arrives.
  const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    const img = styleDivRef.current?.querySelector<HTMLImageElement>(IMAGE_SELECTOR);

    if (!img) return undefined;

    img.addEventListener('load', forceUpdate);

    return () => img.removeEventListener('load', forceUpdate);
  }, [imageModel?.id, styleDivRef]);

  if (!imageModel) return null;

  const objectPosition = imageModel.styles?.object_position || DEFAULT_OBJECT_POSITION;
  const objectFit = imageModel.styles?.object_fit || 'contain';

  const image = () => styleDivRef.current?.querySelector<HTMLImageElement>(IMAGE_SELECTOR);

  const applyPositionPreview = (value: string) => {
    const img = image();
    if (!img) return;

    img.style.objectPosition = value;
  };

  const inertAxes = () => {
    const img = image();

    if (!img) return { x: false, y: false };

    return inertPositionAxes({
      fit: objectFit,
      frameHeight: img.clientHeight,
      frameWidth: img.clientWidth,
      imageHeight: img.naturalHeight,
      imageWidth: img.naturalWidth,
    });
  };

  const { x: disableHorizontal, y: disableVertical } = inertAxes();

  const options = [
    { label: I18n.t('position_top'), value: 'center top' },
    { label: I18n.t('position_bottom'), value: 'center bottom' },
    { label: I18n.t('position_center'), value: 'center center' },
    { label: I18n.t('position_left'), value: 'left center' },
    { label: I18n.t('position_right'), value: 'right center' },
    { label: I18n.t('position_custom'), value: 'custom' },
  ];

  const submitHandler = ({ backgroundPosition, customPosition, customPositionY }: CustomValues) => {
    const value = backgroundPosition === 'custom'
      ? `${customPosition || '50'}% ${customPositionY || '50'}%`
      : backgroundPosition;

    applyPositionPreview(value);

    updateImageModelMutation({ variables: { id: imageModel.id, objectPosition: value } })
      .then((res) => {
        updateSlideObject({ ...slideObject, imageModel: res.data.updateImageModel.result.imageModel });
      })
      .catch((error) => { console.error(error); });
  };

  return (
    <Dropdown
      buttonClass="dropdown-item !tw-flex tw-w-full tw-items-center tw-pr-0 tw-cursor-pointer"
      dropdownPosition="right"
      toggleText={(
        <>
          {I18n.t('change_position')}
          <Icon className="tw-ml-auto tw-mr-2" icon="ChevronRight" size={16} />
        </>
      )}
    >
      <BackgroundPositionManager
        includeVertical
        customMatcher={CUSTOM_RE}
        disableHorizontal={disableHorizontal}
        disableVertical={disableVertical}
        horizontalLabel={I18n.t('position_horizontal')}
        idPrefix={`image-position-${slideObject.id}`}
        initialSliderPosition="50"
        initialSliderPositionY="50"
        livePreview={(values: CustomValues) => (
          applyPositionPreview(`${values.customPosition}% ${values.customPositionY}%`)
        )}
        object={{ backgroundPosition: objectPosition }}
        options={options}
        parseCustom={(value: string) => {
          const [x, y] = value.split(' ');

          return { customPosition: x.replace('%', ''), customPositionY: y.replace('%', '') };
        }}
        submitHandler={submitHandler}
        verticalLabel={I18n.t('position_vertical')}
      />
    </Dropdown>
  );
};

export default ChangeImagePosition;
